import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useProgramContext } from '../../../../hooks/useProgramContext';
import DosenTooltip from '../../../../components/ui/DosenTooltip';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import { Bone } from '../../../../components/ui/DashboardSkeletons';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import StepBar from './components/StepBar';
import StepFooter from './components/StepFooter';
import { LOCAL_STORAGE_KEY, INITIAL_WIZARD_STATE, PROGRAM_DESC_MAX_LENGTH, canNavigateToStep } from './constants';
import { AIMD_STEPS } from './types';
import {
  EditLockBanner,
  ProgramEditLockProvider,
  ReadOnlyWhenLocked,
  editLockMessage,
  setBuildStateHydrating,
  useEditLock,
  useEditLockTarget
} from '../../../../components/program-collaboration';
import type { AIMDv2WizardState, AIMDStepId } from './types';
import { ReactComponent as AiImproveIcon } from '../../../../assets/images/ai-improve.svg';
import RequirementsStep from './steps/RequirementsStep';
import OutcomesStep from './steps/OutcomesStep';
import DocumentsStep from './steps/DocumentsStep';
import PreviewStep from './steps/PreviewStep';
import StructureStep from './steps/StructureStep';
import ContentStep from './steps/ContentStep';
import MembersStep from './steps/MembersStep';
import InviteFinishDialog, { INVITE_FINISH_GATE_COPY, useInviteAndFinish } from './components/InviteFinishDialog';
import ImproveWithAIModal from './components/ImproveWithAIModal';
import { AIMDProvider, useAIMDContext } from './context/AIMDContext';
import { hydrateWizardFromBackend } from './api';
import { BuildLabelsContext, DEFAULT_BUILD_LABELS, useBuildLabels, type BuildLabels } from './buildLabels';
import { LessonTagsContext, useLessonTags, type LessonTags } from './lessonTags';
import { useAimdHeartbeat } from './hooks/useAimdHeartbeat';
import useEditingFrozen from '../../../../hooks/useEditingFrozen';
import useCourseVersioning from '../../../../hooks/useCourseVersioning';
import { VersionHistoryDrawer } from '../Program/components/program tabs/ProgramSettings/versioning/VersionHistoryDrawer';
import { VersionPreview } from '../Program/components/program tabs/ProgramSettings/versioning/VersionPreview';
import { useWizardVersionHistory, WIZARD_VERSION_HINTS } from './versioning/useWizardVersionHistory';
import { generationModulesToDrafts } from './versioning/generationDrafts';
import { EDITING_FROZEN_MESSAGE } from '../../../../utils/constants';
import { EditingFrozenBanner, ReadOnlyWhenFrozen } from '../../../../components/program-freeze/EditingFrozenGuard';
import DueDateModal, { ProgramDueDateData } from '../Program/components/program tabs/settings components/Dialog/DueDateModal';
import { API } from '../../../../api';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { fetchGroupDetails } from '../../../../redux/slices/group-details/groupDetails';
import useProgramCreationFlowV2 from '../../../../hooks/useProgramCreationFlowV2';
import { trackProgramCreated } from '../../../../utils/amplitudeProductTracking';
import { fetchRecommendedDescription } from '../Program/components/learning-framework-builder/useLearningFrameworkStore';

// How far a wizard state has progressed — the furthest of its current and
// highest-completed step. Used to pick the richer source on resume.
export const progressRank = (s: any): number => {
  if (!s || typeof s !== 'object') return -1;
  const cur = typeof s.currentStep === 'number' ? s.currentStep : 0;
  const high = typeof s.highestCompletedStep === 'number' ? s.highestCompletedStep : -1;
  return Math.max(cur, high);
};

// Pick the further-along of the BE-derived state and any localStorage cache for
// THIS group. Guards against a stale BE buildState (e.g. legacy funnel stub)
// overwriting real local progress, while still letting a richer/cross-device BE
// state win. Local cache for a DIFFERENT group is ignored.
// Exported for AddNewProgramDrawer's Continue-resume seeding (same reconcile).
export const mergeWithLocalProgress = (
  beState: AIMDv2WizardState,
  groupId: string
): AIMDv2WizardState => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    const local = raw ? JSON.parse(raw) : null;
    if (local && local.groupId === groupId && progressRank(local) > progressRank(beState)) {
      return local as AIMDv2WizardState;
    }
  } catch {
    // ignore corrupt cache — fall back to the BE state
  }
  return beState;
};

// When `embedded`, the wizard renders inside the Add Program modal (full-screen,
// Structure → Content only): its own StepBar is hidden (the chat stepper drives
// progress), groupId comes from the conversation (not the route), and finishing
// Content calls `onFinish` instead of advancing to the route-based Members step.
type AIMDv2InnerProps = {
  embedded?: boolean;
  embeddedGroupId?: string;
  onFinish?: () => void;
  onStepChange?: (stepId: AIMDStepId) => void;
};

const AIMDv2PageInner: React.FC<AIMDv2InnerProps> = ({
  embedded = false,
  embeddedGroupId,
  onFinish,
  onStepChange
}) => {
  const navigate = useNavigate();
  const { orgId: ctxOrgId, coId: ctxCoId, groupId: routeGroupId, location: dataLocation, isPlatformAdmin } = useProgramContext();
  const ctx = useAIMDContext();
  const { state, currentStepId, validation, isFirstStep, isLastStep, isSaving } = ctx;
  useAimdHeartbeat(ctx.groupId);

  // Structure and Content are shared with co-design admins: one editor at a
  // time. Everyone else gets a read-only view and the banner.
  const lockStage = currentStepId === 'structure' || currentStepId === 'content' ? currentStepId : null;
  useEditLockTarget(ctx.groupId, lockStage);
  const { status: lockStatus, holder: lockHolder } = useEditLock();
  const lockedOut = lockStatus === 'locked';

  // In embedded mode the group comes from the conversation, not the URL.
  const effectiveGroupId = embedded
    ? embeddedGroupId || ''
    : routeGroupId && routeGroupId !== 'admin'
    ? routeGroupId
    : '';

  // Hydrate the wizard when the admin lands on AIMDv2 via the Learning
  // Framework "Continue" link on an existing design-stage program.
  // Without this each visit would treat the URL `:id` as a stranger and
  // either create a brand-new group on save or render the form empty.
  // Sets the wizard's groupId so save/publish target the existing
  // group, and pre-fills programName + audience from the BE doc so the
  // Requirements step renders with the data the admin already provided.
  const reduxDispatch = useAppDispatch();
  const existingGroup = useAppSelector((s) => s.groupDetails.data);
  const orgDisplayName = useAppSelector((s) => s.orgDetails.data?.name);

  // Set the wizard's groupId once when it differs from the URL — avoids
  // resetting wizard state on every render.
  useEffect(() => {
    if (!effectiveGroupId) return;
    if (ctx.groupId === effectiveGroupId) return;
    ctx.setGroupId(effectiveGroupId);
  }, [effectiveGroupId, ctx]);

  // Embedded: report the wizard's current step so the modal's shared stepper
  // reflects Structure / Content.
  useEffect(() => {
    if (embedded) onStepChange?.(currentStepId);
  }, [embedded, currentStepId, onStepChange]);

  // Fetch the group doc once per routeGroupId, regardless of whether
  // the wizard already had a matching groupId in localStorage. Without
  // this the Redux groupDetails stays empty when the wizard restores a
  // matching groupId from a previous session, so the hydration below
  // (programName + audience) would silently skip.
  const fetchedForGroupIdRef = useRef<string | null>(null);
  useEffect(() => {
    // Embedded: state is seeded from the conversation; no route-based fetch.
    if (embedded) return;
    if (!routeGroupId || routeGroupId === 'admin') return;
    if (fetchedForGroupIdRef.current === routeGroupId) return;
    fetchedForGroupIdRef.current = routeGroupId;
    reduxDispatch(fetchGroupDetails({ grpId: routeGroupId, loc: dataLocation, useCache: false }));
  }, [embedded, routeGroupId, reduxDispatch, dataLocation]);

  // One-shot hydration guard keyed by routeGroupId — re-hydrates only
  // when the admin enters AIMDv2 for a different program. Avoids
  // overwriting the user's in-progress edits and removes the race
  // between async fetchGroupDetails and per-field nullish checks (the
  // previous logic would skip programName if state.requirements
  // already had any value, which left the field empty when audience —
  // dispatched in the same patch — succeeded).
  const hydratedForGroupIdRef = useRef<string | null>(null);
  useEffect(() => {
    // Embedded: requirements are already seeded from the conversation.
    if (embedded) return;
    if (!routeGroupId || routeGroupId === 'admin') return;
    if (!existingGroup) return;
    // The /group/{grpId} payload exposes the id as `grpId` (string) and
    // sometimes also `_id`. Compare against the most reliable signal —
    // grpId — so we don't early-return when only `_id` is missing.
    const incomingId =
      (existingGroup as any).grpId ||
      (existingGroup as any)._id?.toString?.() ||
      '';
    if (incomingId !== routeGroupId) return;
    if (hydratedForGroupIdRef.current === routeGroupId) return;
    const patch: { programName?: string; audience?: string[] } = {};
    const name = (existingGroup as any).name;
    if (name) patch.programName = name;
    // Audience is fetched separately by RequirementsStep
    // (getGroupMemberAttributes); this payload doesn't include it.
    if (Object.keys(patch).length > 0) {
      ctx.updateRequirements(patch);
      hydratedForGroupIdRef.current = routeGroupId;
    }
  }, [routeGroupId, existingGroup, ctx]);

  // A program added from the Learning Framework opens with the description
  // Elliott recommended (what the program should include). The group doc has no
  // description, so it comes from the framework's recommendation → program link.
  // One-shot per program, and never over anything the admin has already written.
  const descriptionRef = useRef(state.requirements.programDescription);
  descriptionRef.current = state.requirements.programDescription;
  const updateRequirementsRef = useRef(ctx.updateRequirements);
  updateRequirementsRef.current = ctx.updateRequirements;
  const descriptionForGroupIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (embedded) return;
    if (!routeGroupId || routeGroupId === 'admin') return;
    if (descriptionForGroupIdRef.current === routeGroupId) return;
    descriptionForGroupIdRef.current = routeGroupId;
    if (descriptionRef.current?.trim()) return;
    fetchRecommendedDescription({ groupId: routeGroupId, orgId: ctxOrgId, isPlatformAdmin }).then((description) => {
      if (!description || descriptionRef.current?.trim()) return;
      updateRequirementsRef.current({ programDescription: description.slice(0, PROGRAM_DESC_MAX_LENGTH) });
    });
  }, [embedded, routeGroupId, ctxOrgId, isPlatformAdmin]);

  // Creation-flow revamp gate (Publish label, embedded Members step,
  // Invite & Finish deferred enrollment). Off = legacy behaviour.
  const creationFlowV2 = useProgramCreationFlowV2();
  // What the steps are called: the wizard's own wording unless whatever embeds it names them.
  const buildLabels = useBuildLabels();
  // The Capability build flow: what the program's lessons are for (null elsewhere).
  const lessonTags = useLessonTags();
  // Relaunch editing freeze (B4-2): Structure/Content render read-only, footer save is off.
  const editingFrozen = useEditingFrozen();

  const [attemptedNext, setAttemptedNext] = useState(false);
  const [improveProgramOpen, setImproveProgramOpen] = useState(false);
  const [makingChanges, setMakingChanges] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // Members step: Invite & Finish stays disabled until an audience is set or
  // someone was invited by name (reported by the Members page).
  const [membersReady, setMembersReady] = useState(false);
  // §6 — Invite & Finish: confirm with a live match count, then enrol the stored
  // audience (the program becomes Invited). Embedded → hand back to the modal
  // (closes onto the dashboard); standalone → the dashboard with the highlight hint.
  const handleInviteFinished = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    if (embedded) {
      onFinish?.();
      return;
    }
    navigate(`/app/programdetails/${ctxOrgId}/${ctxCoId}/admin/overview`, {
      state: { highlightProgramId: ctx.groupId }
    });
  }, [embedded, onFinish, navigate, ctxOrgId, ctxCoId, ctx.groupId]);
  const inviteFinish = useInviteAndFinish(ctx.groupId, handleInviteFinished);
  const [contentDirty, setContentDirty] = useState(false);
  const [isVideoGenerating, setIsVideoGenerating] = useState(false);

  // Course versioning (org flag, docs §2b): Version History for the
  // generation on the Structure and Content steps, never while the org is
  // frozen. The preview is laid over the step (the step stays mounted, so
  // its state — selected lesson, running video jobs — survives); a restore
  // reloads the wizard from GET program-generation/{id}/status.
  const versioningOn = useCourseVersioning();
  const isStructureOrContent = currentStepId === 'structure' || currentStepId === 'content';
  const versioningEnabled = versioningOn && !editingFrozen && Boolean(state.generationId);
  const showVersionHistory = versioningEnabled && isStructureOrContent;
  const currentStepIdRef = useRef(currentStepId);
  currentStepIdRef.current = currentStepId;
  const wizardVersions = useWizardVersionHistory({
    generationId: state.generationId,
    enabled: versioningEnabled,
    beforeRestore: async () => {
      // The BE keeps a before_restore version of the artifact: flush what the
      // admin has in the browser first, the same way Save Changes does.
      if (currentStepIdRef.current === 'content') {
        try {
          await ctx.handleSaveStructure();
        } catch (err) {
          // A structure the backend refuses (a capability left with no lesson): fix it first.
          toast.error((err as Error).message);
          return false;
        }
      }
      const result = await ctx.handleSaveContent();
      return result.ok;
    },
    onRestored: async () => {
      const { hasContent } = await ctx.reloadGenerationFromServer();
      setContentDirty(false);
      if (currentStepIdRef.current === 'content' && !hasContent) {
        // A structure-only version (e.g. "Before AI change" taken before the
        // content was written): the Content step would sit empty, so send the
        // admin back to Structure where "Generate Content Now" writes it again.
        ctx.goToStep(AIMD_STEPS.indexOf('structure'));
        toast.info(
          'That version has no Lesson Body yet, so you are back on the Structure step. Build Lessons Now writes it again.'
        );
      }
    }
  });
  const previewing = showVersionHistory && Boolean(wizardVersions.previewEntry);
  const currentModules = (state.content.generatedContent as any)?.modules ?? state.structure.generatedStructure?.modules;
  const currentDrafts = useMemo(() => generationModulesToDrafts(currentModules), [currentModules]);
  const { close: closeVersionHistory } = wizardVersions;
  useEffect(() => {
    if (!showVersionHistory) closeVersionHistory();
  }, [showVersionHistory, closeVersionHistory]);
  const [programDueDateOpen, setProgramDueDateOpen] = useState(false);
  const [programDueDate, setProgramDueDate] = useState<ProgramDueDateData | null>(null);

  const handleImproveProgramSubmit = useCallback(async (feedback: string) => {
    setImproveProgramOpen(false);
    setMakingChanges(true);
    try {
      await ctx.handleImproveProgram(feedback);
    } finally {
      setMakingChanges(false);
    }
  }, [ctx]);

  // Content step "Save Changes": titles through the structure endpoint (as
  // before) and the edited lesson bodies / quizzes / materials through the
  // content endpoint, which is what Publish materialises. Resolves false
  // when the content save failed — the dirty lessons are kept for a retry
  // and the step stays unsaved.
  const saveContentStep = useCallback(async (): Promise<boolean> => {
    if (editingFrozen) {
      toast.warn(EDITING_FROZEN_MESSAGE);
      return false;
    }
    try {
      await ctx.handleSaveStructure();
    } catch (err) {
      // A structure the backend refuses (a capability left with no lesson): nothing more is saved.
      toast.error((err as Error).message);
      return false;
    }
    const result = await ctx.handleSaveContent();
    if (!result.ok) {
      // 423 (editing frozen) is already toasted by the axios interceptor.
      if (result.status !== 423) {
        toast.error(result.message || "Your changes couldn't be saved. Please try again.");
      }
      return false;
    }
    // The buildState is what a co-design admin's reload (after the edit lock
    // frees) and a resume on another device read first: keep its lesson
    // content in step with what was just saved, not just the titles.
    try {
      await ctx.persistBuildState();
    } catch (err) {
      console.error('[AIMDv2] content buildState save failed', err);
      toast.error('Your lesson changes could not be saved. Please try again.');
      return false;
    }
    setContentDirty(false);
    // A save that wrote a version shows up in an open Version History panel right away.
    if (result.versionCreated && wizardVersions.panelOpen) wizardVersions.refresh();
    return true;
  }, [ctx, editingFrozen, wizardVersions]);

  const handleNext = useCallback(async () => {
    setAttemptedNext(true);

    // Structure step: advance to Content and let ContentStep auto-trigger generation
    if (currentStepId === 'structure') {
      if (lessonTags) {
        // The Capability build flow: the structure is saved first, so one that leaves a capability
        // with no lesson is refused here, with the backend's reason, before any content is written.
        try {
          await ctx.handleSaveStructure();
        } catch (err) {
          toast.error((err as Error).message);
          return;
        }
      }
      const didAdvance = ctx.goNext();
      if (didAdvance) setAttemptedNext(false);
      return;
    }

    // Content step: publish then advance
    if (currentStepId === 'content') {
      setPublishing(true);
      try {
        await ctx.handlePublish();
        // ATC: this button is the "Create" click that ends the creation wizard,
        // so the clock the admin is measured on stops HERE — not when the
        // program is later invited. trackProgramCreated freezes the BE timer
        // (flushing the tail the 30s heartbeat hasn't sent) and emits the one
        // per-creation row: course name, admin email, status, time taken.
        await trackProgramCreated({
          grpId: ctx.groupId,
          programName: state.requirements.programName || existingGroup?.name || 'Program',
          programModel: 'Masterclass',
          creationFlow: 'AI MD',
          organizationName: orgDisplayName || ''
        });
        if (programDueDate && ctx.groupId && ctxOrgId) {
          // Own try/catch: the program is already published at this point,
          // so a due-date failure must not strand the wizard on this step.
          try {
            const formData = new FormData();
            // JSON-stringified object for the BE Joi validator (busboy can't
            // decode bracket notation).
            formData.append(
              'programDueDate',
              JSON.stringify({
                existingMembers: {
                  type: programDueDate.existingMembers.type,
                  value: Number(programDueDate.existingMembers.value) || 0
                },
                newMembers: {
                  type: programDueDate.newMembers.type,
                  value: Number(programDueDate.newMembers.value) || 0
                }
              })
            );
            await API.updateGroupInfo({ orgId: ctxOrgId, grpId: ctx.groupId, formData });
          } catch (dueDateErr) {
            console.error('[AIMDv2] program due date save failed', dueDateErr);
            toast.error("The program was published, but the due date couldn't be saved. You can set it again from the program's Design tab.");
          }
        }
        // Legacy embedded (in-modal) flow ends at Content — skip the
        // route-based Members step and hand control back to the modal to
        // finish/close. With the creation-flow revamp on, embedded advances
        // through Members inside the same modal like the standalone flow (§2).
        if (embedded && !creationFlowV2) {
          onFinish?.();
          return;
        }
        const didAdvance = ctx.goNext();
        if (didAdvance) setAttemptedNext(false);
      } catch (err) {
        console.error('[AIMDv2] publish failed', err);
        const body = (err as any)?.response?.data;
        if (body?.code === 'ROLEPLAY_NOT_WRITTEN') {
          // A Scored Roleplay on but never written can't go live (docs/PRD-capability-lesson-roleplays.md R6).
          const lessons: string[] = (body.lessons || []).map((l: { lesson: string }) => `"${l.lesson}"`);
          toast.error(
            `This course can't be created yet: ${lessons.join(', ')} ${
              lessons.length === 1 ? 'has its' : 'have their'
            } Roleplay on, but it hasn't been written. Open the lesson and generate it, or switch the Roleplay off.`
          );
        } else {
          toast.error(body?.message || (err as any)?.message || 'The program could not be published. Please try again.');
        }
      } finally {
        setPublishing(false);
      }
      return;
    }

    // Members (last step). Legacy: clear wizard state and navigate back to the
    // dashboard — no enrollment ever fired here. Revamp (§6): "Invite & Finish"
    // opens a confirmation carrying the LIVE audience match count, and the
    // actual enrollment happens on confirm.
    if (currentStepId === 'members') {
      if (!creationFlowV2) {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        navigate(`/app/programdetails/${ctxOrgId}/${ctxCoId}/admin/overview`);
        return;
      }
      inviteFinish.openConfirm();
      return;
    }

    // Documents step: surface why Next is blocked (selected docs still indexing)
    // — goNext() silently returns false on invalid steps, so toast the reason.
    if (currentStepId === 'documents' && !validation.isValid) {
      toast.error(validation.errors._global || 'Wait for selected documents to finish indexing.');
      return;
    }

    const didAdvance = ctx.goNext();
    if (didAdvance) {
      setAttemptedNext(false);
    }
  }, [
    ctx,
    currentStepId,
    validation,
    state.currentStep,
    state.requirements.programName,
    existingGroup?.name,
    orgDisplayName,
    navigate,
    embedded,
    onFinish,
    creationFlowV2,
    inviteFinish,
    lessonTags
  ]);

  const handlePrev = useCallback(() => {
    setAttemptedNext(false);
    ctx.goPrev();
  }, [ctx]);

  const handleStepClick = useCallback(
    (index: number) => {
      setAttemptedNext(false);
      ctx.goToStep(index);
    },
    [ctx]
  );

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const getNextLabel = (): string => {
    switch (currentStepId) {
      case 'structure':
        return buildLabels.generateContent;
      case 'content':
        // §5 — publishing creates the program without enrolling anyone; the
        // legacy 'Create' copy stays until the revamp flag rolls out.
        return creationFlowV2 ? 'Publish' : 'Build';
      case 'members':
        return creationFlowV2 ? 'Enroll People' : 'Finish';
      default:
        return 'Next';
    }
  };

  // Next button label while the footer is busy (no spinner, just the label).
  const getSavingLabel = (): string => {
    if (inviteFinish.finishing) return 'Enrolling...';
    if (publishing) return creationFlowV2 ? 'Publishing...' : 'Building...';
    if (ctx.isGenerating) return 'Building...';
    return 'Saving...';
  };

  const contentGeneration = state.content.generatedContent as any;
  const isContentComplete = currentStepId === 'content' &&
    contentGeneration?.status === 'completed';

  const allLessonsReady = (() => {
    if (ctx.isGenerating || ctx.pollingActive) return false;
    if (contentGeneration?.status !== 'completed') return false;
    const modules = contentGeneration?.modules;
    if (!modules || modules.length === 0) return false;
    return modules.every((m: any) =>
      m.lessons?.length > 0 && m.lessons.every((l: any) => l.status === 'completed')
    );
  })();

  /**
   * Why the primary button is disabled, in the user's words.
   *
   * It can be disabled for four different reasons but only two of them used to
   * say anything, so unsaved edits and still-generating content left the button
   * dead with nothing on screen to explain it or say what to do next. The order
   * matters: report the condition the user has to clear first.
   */
  const nextDisabledReason = ((): string | undefined => {
    if (currentStepId === 'content') {
      if (isVideoGenerating) return 'Your AI video is being generated, you can save once ready.';
      if (contentDirty) return 'Save your changes first, then you can publish.';
      if (!isContentComplete) {
        return 'Elliott is still writing this program. You can publish once every lesson is ready.';
      }
    }
    if (creationFlowV2 && currentStepId === 'members' && !membersReady) {
      return INVITE_FINISH_GATE_COPY;
    }
    return undefined;
  })();

  const renderStep = () => {
    switch (currentStepId) {
      case 'requirements':
        return (
          <RequirementsStep
            data={state.requirements}
            validation={validation}
            showErrors={attemptedNext}
            onChange={ctx.updateRequirements}
            groupId={ctx.groupId}
            onOrgAttributeGroupsLoaded={ctx.setOrgAttributeGroups}
            onAudienceAvailabilityChange={ctx.setAudienceAvailable}
          />
        );

      case 'outcomes':
        return (
          <OutcomesStep
            data={state.outcomes}
            validation={validation}
            showErrors={attemptedNext}
            onChange={ctx.updateOutcomes}
          />
        );

      case 'documents':
        return (
          <DocumentsStep
            data={state.documents}
            onChange={ctx.updateDocuments}
            groupId={ctx.groupId}
          />
        );

      case 'preview':
        return <PreviewStep />;

      case 'structure':
        return (
          <ReadOnlyWhenFrozen frozen={editingFrozen}>
            <StructureStep />
          </ReadOnlyWhenFrozen>
        );

      case 'content':
        return (
          <ReadOnlyWhenFrozen frozen={editingFrozen}>
            <ContentStep
              onMarkDirty={() => setContentDirty(true)}
              onSave={async () => {
                await saveContentStep();
              }}
              onVideoGeneratingChange={setIsVideoGenerating}
            />
          </ReadOnlyWhenFrozen>
        );

      case 'members':
        return <MembersStep onInviteReadinessChange={setMembersReady} />;

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        // Embedded: fill the modal body. Page: full viewport minus chrome.
        width: embedded ? '100%' : 'calc(100% + 50px)',
        height: embedded ? '100%' : 'calc(100vh - 86px)',
        m: embedded ? 0 : '-25px',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--Fill-Background, #0F0F0F)',
        overflow: 'hidden',
        // Keep the footer reachable beside the persistent Version History panel.
        pr: wizardVersions.panelOpen ? '380px' : 0,
        transition: 'padding-right 225ms ease'
      }}
    >
      <EditingFrozenBanner frozen={editingFrozen} message={EDITING_FROZEN_MESSAGE} sx={{ mx: 3, mt: 2 }} />

      {/* Step bar row — hidden when embedded (the chat stepper drives progress). */}
      <Box sx={{ px: 3, pt: 3, pb: 2, display: embedded ? 'none' : 'flex', alignItems: 'center', gap: 2 }}>
        {/* {currentStepId !== 'requirements' && (
          <Box
            component="input"
            value={state.requirements.programName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              ctx.updateRequirements({ programName: e.target.value })
            }
            placeholder="Program Name"
            sx={{
              width: 200, flexShrink: 0,
              background: 'transparent',
              border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
              borderRadius: '8px', px: 1.5, py: 1,
              color: 'var(--Text-text-primary, #FDFDFD)',
              fontFamily: 'Inter', fontSize: 13, fontWeight: 500,
              outline: 'none',
              '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
            }}
          />
        )} */}
        <Box sx={{ flex: 1 }}>
          <StepBar
            currentStep={state.currentStep}
            highestCompletedStep={state.highestCompletedStep}
            onStepClick={handleStepClick}
          />
        </Box>
      </Box>

      {/* Co-design lock banner (Invite Admin sits in line with each step's title). */}
      {isStructureOrContent && lockedOut && (
        <Box sx={{ px: 3, pt: embedded ? 2 : 0, pb: 2 }}>
          <EditLockBanner />
        </Box>
      )}

      {/* Step content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: isStructureOrContent ? 'stretch' : 'center',
          px: 3,
          py: isStructureOrContent ? 0 : 4
        }}
      >
        {previewing && (
          <VersionPreview
            draft={currentDrafts}
            source={wizardVersions.preview}
            restoreDisabledReason="Restoring this version…"
          />
        )}
        {/* Hidden, not unmounted, while a version is previewed: the step keeps its state. */}
        <ReadOnlyWhenLocked>
          <Box sx={{ display: previewing ? 'none' : 'contents' }}>{renderStep()}</Box>
        </ReadOnlyWhenLocked>
      </Box>

      {/* Footer navigation */}
      <StepFooter
        isFirstStep={isFirstStep}
        isLastStep={isLastStep}
        isSaving={isSaving || ctx.isGenerating || publishing || inviteFinish.finishing}
        savingLabel={getSavingLabel()}
        onCancel={handleCancel}
        onPrev={handlePrev}
        onNext={handleNext}
        nextLabel={getNextLabel()}
        saveAction={currentStepId === 'content' && !lockedOut ? {
          label: 'Save Changes',
          onClick: async () => {
            if (await saveContentStep()) toast.success('Saved successfully');
          },
          active: contentDirty && allLessonsReady && !isVideoGenerating && !editingFrozen
        } : undefined}
        saveDisabledTooltip={
          currentStepId === 'content' && editingFrozen
            ? EDITING_FROZEN_MESSAGE
            : currentStepId === 'content' && isVideoGenerating
              ? 'Your AI video is being generated, you can save once ready.'
              : undefined
        }
        nextDisabled={
          lockedOut ||
          (currentStepId === 'content' && (!isContentComplete || contentDirty || isVideoGenerating)) ||
          (creationFlowV2 && currentStepId === 'members' && !membersReady)
        }
        nextDisabledTooltip={lockedOut ? editLockMessage(lockHolder?.name) : nextDisabledReason}
        nextTooltip={
          creationFlowV2 && currentStepId === 'content'
            ? 'Publishing will not enroll learners. This will be handled at the next step.'
            : undefined
        }
        rightExtraContent={(currentStepId === 'content' && !lockedOut) || showVersionHistory ? (
          <>
            {showVersionHistory && (
                <DosenTooltip title="Version History">
                <IconButton
                  onClick={wizardVersions.toggle}
                  size="small"
                  aria-label="Version History"
                  aria-pressed={wizardVersions.panelOpen}
                  sx={{ color: wizardVersions.panelOpen ? '#F79420' : '#FDFDFD' }}
                >
                  <HistoryIcon sx={{ fontSize: 28 }} />
                </IconButton>
                </DosenTooltip>
            )}
            {/* Due date edits are the editor's: hidden for a read-only viewer. */}
            {currentStepId === 'content' && !lockedOut && (
                <DosenTooltip
                title={
                  programDueDate &&
                  (programDueDate.existingMembers.type !== 'activeNow' ||
                    programDueDate.newMembers.type !== 'activeNow')
                    ? 'Edit Program Due Date'
                    : 'Add Program Due Date'
                }
              >
                <IconButton onClick={() => setProgramDueDateOpen(true)} size="small" sx={{ color: '#FDFDFD' }}>
                  <CalendarMonthIcon sx={{ fontSize: 28 }} />
                </IconButton>
                </DosenTooltip>
            )}
          </>
        ) : undefined}
        leftContent={lockedOut ? (
          <span />
        ) : !isFirstStep && !canNavigateToStep(state.currentStep, state.currentStep - 1) && !isStructureOrContent ? (
          // No "Previous" once past Structure (e.g. on Members).
          <span />
        ) : (currentStepId === 'structure' || currentStepId === 'content') ? (
          <Box
            component="button"
            onClick={currentStepId === 'content' && !allLessonsReady ? undefined : () => setImproveProgramOpen(true)}
            disabled={currentStepId === 'content' && !allLessonsReady}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              background: 'transparent',
              border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
              borderRadius: '8px', px: 2.5, py: 1.5,
              cursor: currentStepId === 'content' && !allLessonsReady ? 'default' : 'pointer',
              color:
                currentStepId === 'content' && !allLessonsReady ? '#565656' : 'var(--Text-text-primary, #FDFDFD)',
              fontFamily: 'Inter', fontSize: 13, fontWeight: 500,
              // Disabled reads dark with dim text, not faded.
              ...(currentStepId === 'content' && !allLessonsReady && { background: 'rgba(0, 0, 0, 0.2)' }),
              pointerEvents: currentStepId === 'content' && !allLessonsReady ? 'none' : 'auto',
              '&:hover': { borderColor: 'var(--Stroke-highlight, #F79420)' }
            }}
          >
            <AiImproveIcon style={{ width: 18, height: 18 }} />
              {currentStepId === 'structure' ? 'Improve Structure with Elliott' : 'Improve Program with Elliott'}
          </Box>
        ) : undefined}
      />

      {/* Invite & Finish confirmation (§6) — live match count, enrollment on confirm */}
      <InviteFinishDialog {...inviteFinish.dialogProps} />

      {/* Version History (course versioning flag) — generation versions, same panel as the Designer */}
      {versioningEnabled && (
        <VersionHistoryDrawer
          source={wizardVersions.drawer}
          hints={WIZARD_VERSION_HINTS}
          currentLabel="Current program"
          currentHint="What you’re building now"
        />
      )}

      {/* Program Due Date Modal */}
      <DueDateModal
        open={programDueDateOpen}
        onClose={() => setProgramDueDateOpen(false)}
        mode="program"
        initialProgramData={programDueDate || undefined}
        onSave={() => {}}
        onSaveProgram={(data) => {
          setProgramDueDate(data);
          setProgramDueDateOpen(false);
        }}
      />

      {/* Improve Program Structure modal */}
      <ImproveWithAIModal
        variant="program"
        open={improveProgramOpen}
        onClose={() => setImproveProgramOpen(false)}
        onSubmit={handleImproveProgramSubmit}
      />

      {/* "Making changes" loading modal */}
      <Dialog
        open={makingChanges}
        PaperProps={{
          sx: {
            background: '#1A1A1A',
            borderRadius: '12px',
            border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
            p: 3, minWidth: 360
          }
        }}
      >
        <Typography sx={{ fontSize: 22, fontWeight: 700, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)', mb: 2 }}>
          Making changes
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HourglassBottomIcon sx={{
            fontSize: 18,
            color: 'var(--Text-text-secondary, #B6B6B6)',
            animation: 'hourglass-spin 2s ease-in-out infinite',
            '@keyframes hourglass-spin': {
              '0%, 40%': { transform: 'rotate(0deg)' },
              '60%, 100%': { transform: 'rotate(180deg)' },
            }
          }} />
          <Typography sx={{ fontSize: 14, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)' }}>
            Please wait as we improve the program {currentStepId === 'content' ? 'content' : 'structure'}...
          </Typography>
        </Box>
      </Dialog>
    </Box>
  );
};

const AIMDv2Page: React.FC = () => {
  const params = useParams<{ id?: string }>();
  const location = useLocation();
  const routeGroupId = params?.id && params.id !== 'admin' ? params.id : '';

  // Explicit resume intent — set by the dashboard "Continue" action when the
  // admin reopens a specific design-stage AI program. When present we skip the
  // "Resume Program?" prompt and seed the wizard straight from the BE record.
  const resumeIntent =
    (location.state as { resume?: boolean } | null)?.resume === true ||
    new URLSearchParams(location.search).get('resume') === '1';
  const canHydrateFromBE = resumeIntent && Boolean(routeGroupId);

  const [hydrating, setHydrating] = useState<boolean>(canHydrateFromBE);

  // One-shot sanitisation (runs before the provider reads localStorage):
  // if the cached wizard state belongs to a DIFFERENT program than the one in
  // the route — e.g. the admin starts a brand-new AI build while a previous,
  // unfinished one is still in localStorage — discard it and seed a clean
  // state bound to this program's groupId. Without this the new program would
  // inherit the old group/generation/content and publish over the wrong one,
  // leaving the program the admin actually built stuck in "design" with no
  // model. The resume path (canHydrateFromBE) seeds from the BE instead.
  useState(() => {
    if (canHydrateFromBE || !routeGroupId) return null;
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      const saved = raw ? JSON.parse(raw) : null;
      if (saved?.groupId && saved.groupId !== routeGroupId) {
        localStorage.setItem(
          LOCAL_STORAGE_KEY,
          JSON.stringify({ ...INITIAL_WIZARD_STATE, groupId: routeGroupId })
        );
      }
    } catch {
      // ignore — corrupt cache will be replaced by the provider's fallback
    }
    return null;
  });

  const [showResumeModal, setShowResumeModal] = useState<boolean>(() => {
    if (resumeIntent) return false; // explicit resume → never prompt
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!raw) return false;
      const saved = JSON.parse(raw);
      return saved && (saved.currentStep > 0 || saved.generationId);
    } catch {
      return false;
    }
  });
  const [decided, setDecided] = useState(!showResumeModal && !canHydrateFromBE);

  // Resume hydration: fetch THIS program's buildState from the BE and seed the
  // wizard's localStorage before the provider mounts, so it reopens on the
  // saved step with the generated values — and resumes the right program
  // regardless of what was left in localStorage from another one.
  useEffect(() => {
    if (!canHydrateFromBE) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const beState = await hydrateWizardFromBackend(routeGroupId);
        if (cancelled) return;
        // Reconcile with any locally-cached progress for THIS group. The BE
        // buildState can lag localStorage — e.g. an earlier aimd-v2 save didn't
        // land and the group still holds the legacy funnel stub — so keep
        // whichever source got further. Never wipe real local progress with a
        // step-0 hydrate; but a richer/cross-device BE state still wins.
        const merged = mergeWithLocalProgress(beState, routeGroupId);
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(merged));
      } catch (err) {
        console.error('[AIMDv2] resume hydration failed', err);
      } finally {
        if (!cancelled) {
          setHydrating(false);
          setDecided(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canHydrateFromBE, routeGroupId]);

  const handleContinue = useCallback(() => {
    setShowResumeModal(false);
    setDecided(true);
  }, []);

  const handleStartNew = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    setShowResumeModal(false);
    setDecided(true);
  }, []);

  if (hydrating) {
    return (
      <Box
        sx={{
          width: 'calc(100% + 50px)', height: 'calc(100vh - 86px)',
          m: '-25px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'var(--Fill-Background, #0F0F0F)'
        }}
      >
        <WizardSkeleton />
      </Box>
    );
  }

  if (!decided) {
    return (
      <Box
        sx={{
          width: 'calc(100% + 50px)', height: 'calc(100vh - 86px)',
          m: '-25px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'var(--Fill-Background, #0F0F0F)'
        }}
      >
        <Dialog
          open={showResumeModal}
          PaperProps={{
            sx: {
              background: '#1A1A1A',
              borderRadius: '12px',
              border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
              p: 4, maxWidth: 440, width: '100%'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}>
              Resume Program?
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 14, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', lineHeight: '22px', mb: 3 }}>
            You have an unfinished program in progress. Would you like to continue where you left off or start fresh?
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <DosenButton tone="basic" sizeVariant="lg" onClick={handleStartNew} sx={{ flex: 1 }}>
              Start New
            </DosenButton>
            <DosenButton tone="cta" sizeVariant="lg" onClick={handleContinue} sx={{ flex: 1 }}>
              Open
            </DosenButton>
          </Box>
        </Dialog>
      </Box>
    );
  }

  return (
    <ProgramEditLockProvider grpId={routeGroupId}>
      <ReloadWhenLockRegained>
        <AIMDProvider>
          <AIMDv2PageInner />
        </AIMDProvider>
      </ReloadWhenLockRegained>
    </ProgramEditLockProvider>
  );
};

// While another admin held the lock this admin was looking at a read-only
// snapshot. When the lock comes back, reload the wizard from the BE (the other
// admin's saved work) and remount it, so no one edits — or saves over — a stale
// copy. The lock provider sits above, so the remount keeps the lock.
const ReloadWhenLockRegained: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { regainedCount, grpId } = useEditLock();
  const [generation, setGeneration] = useState(0);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (!regainedCount || !grpId) return undefined;
    let cancelled = false;
    setReloading(true);
    // Close the write gate until the reload lands: nothing may save the stale
    // snapshot over the other admin's work in the meantime.
    setBuildStateHydrating(grpId, true);
    hydrateWizardFromBackend(grpId)
      .then((beState) => {
        if (!cancelled) localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(beState));
      })
      .catch((err) => console.error('[AIMDv2] reload after lock regained failed', err))
      .finally(() => {
        setBuildStateHydrating(grpId, false);
        if (cancelled) return;
        setReloading(false);
        setGeneration((g) => g + 1);
        toast.info('You are back in the editor, with the latest saved changes.');
      });
    return () => {
      cancelled = true;
    };
  }, [regainedCount]); // eslint-disable-line react-hooks/exhaustive-deps

  if (reloading) {
    return (
      <Box sx={{ width: '100%', height: '100%', minHeight: 320, display: 'flex' }}>
        <WizardSkeleton />
      </Box>
    );
  }
  return <React.Fragment key={generation}>{children}</React.Fragment>;
};

// Dark placeholder shaped like the wizard (step bar, then the step's two
// columns) while a program opens or reloads, instead of a spinner.
const WizardSkeleton: React.FC = () => (
  <Box
    aria-busy
    aria-label="Loading"
    sx={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      p: 3,
      boxSizing: 'border-box'
    }}
  >
    <Box sx={{ display: 'flex', gap: '12px' }}>
      {Array.from({ length: 5 }, (_, i) => (
        <Bone key={i} width={120} height={28} radius="14px" />
      ))}
    </Box>
    <Box sx={{ flex: 1, display: 'flex', gap: '24px', minHeight: 0 }}>
      <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {Array.from({ length: 6 }, (_, i) => (
          <Bone key={i} height={44} />
        ))}
      </Box>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Bone width="40%" height={28} />
        <Bone height={18} />
        <Bone height={18} />
        <Bone width="70%" height={18} />
        <Bone height={220} />
      </Box>
    </Box>
  </Box>
);

// Embeddable wizard for the Add Program modal. The modal seeds the wizard's
// localStorage state (currentStep=structure + the conversation inputs) before
// mounting this, so the provider hydrates straight onto the Structure step.
// Renders embedded (full-screen in the modal): no StepBar, groupId from the
// conversation, and finishing Content calls onFinish instead of routing on.
export const AIMDEmbeddedWizard: React.FC<{
  groupId: string;
  onFinish: () => void;
  onStepChange?: (stepId: AIMDStepId) => void;
  /** What the Structure step and its button are called here, where that differs from the wizard's own wording. */
  labels?: Partial<BuildLabels>;
  /** The Capability build flow: the program's capabilities and outcomes, so each lesson can say which it is for. */
  lessonTags?: LessonTags | null;
}> = ({ groupId, onFinish, onStepChange, labels, lessonTags = null }) => {
  const buildLabels = React.useMemo(
    () => ({ ...DEFAULT_BUILD_LABELS, ...labels }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [labels?.structureTitle, labels?.generateContent]
  );
  return (
    <BuildLabelsContext.Provider value={buildLabels}>
      <LessonTagsContext.Provider value={lessonTags}>
        <ProgramEditLockProvider grpId={groupId}>
          <ReloadWhenLockRegained>
            <AIMDProvider embedded>
              <AIMDv2PageInner embedded embeddedGroupId={groupId} onFinish={onFinish} onStepChange={onStepChange} />
            </AIMDProvider>
          </ReloadWhenLockRegained>
        </ProgramEditLockProvider>
      </LessonTagsContext.Provider>
    </BuildLabelsContext.Provider>
  );
};

export default AIMDv2Page;
